// The email in a Bailey plan: a salon template for the look (the salon's
// brand, logo and footer), with Bailey's own words in it. The templates' other
// sections carry example details (an offer code, a stylist called Maya, "£50"),
// so a plan keeps only the header, the lead section and the footer: nothing in
// an email Bailey sends is made up.
import { findTemplate } from '../../email-composer/data/templates'
import { buildTemplateBlocks } from '../../email-composer/email/apply'
import { renderEmail } from '../../email-composer/email/render'
import { resolveLocalLinks } from '../../email-composer/email/links'

export const TEMPLATE_FOR = {
  fill: { hair: 'hair-quiet-days', barber: 'barber-midweek', beauty: 'beauty-nails-offer' },
  moment: { hair: 'hair-gift', barber: 'barber-gift', beauty: 'beauty-gift' },
  winback: { hair: 'hair-winback', barber: 'barber-rebook', beauty: 'beauty-infill' },
  intro: { hair: 'hair-new-stylist', barber: 'barber-new-barber', beauty: 'beauty-newsletter' },
}
export const templateFor = (kind, market = 'hair') => (TEMPLATE_FOR[kind] || TEMPLATE_FOR.moment)[market] || TEMPLATE_FOR[kind].hair

let seq = 0
const textBlock = (body) => ({ id: `s-text-bailey-${++seq}`, type: 's-text', content: { eyebrow: '', heading: '', body, align: 'center', tone: 'paper', size: 's', py: 's' } })

// { blocks, subject, preheader, html } for a plan's email item
//   email: { templateId, subject, preheader, hero: { eyebrow, headline, body, button, buttonUrl, image? }, note }
export const emailFor = (email, { market = 'hair', kit, links, salonName = '' } = {}) => {
  const tpl = findTemplate(email.templateId, market) || findTemplate(templateFor('moment', market), market)
  const built = buildTemplateBlocks(tpl, { market, kit, links })
  let heroDone = false
  const kept = []
  built.forEach((b) => {
    if (b.type === 's-header') kept.push(b)
    else if (b.type === 's-hero' && !heroDone) {
      heroDone = true
      // Only the words this email gives. A template's own eyebrow, words and
      // button are its example ("This week only", "take 20% off", "Book with
      // Marcus"), so anything not given is left empty or plain, never theirs
      const given = email.hero || {}
      const hero = {
        ...b.content,
        eyebrow: '', body: '', button: 'Book now', buttonUrl: '{{BookingLink}}',
        ...given,
        headline: given.headline || email.subject || b.content.headline,
      }
      if (email.hero && email.hero.image) hero.variant = hero.variant === 'split' ? 'split' : 'frame'
      // No photo of their own: the words-only hero. The template's picture is
      // an example (a stock blow-dry, "Jess, our new colour specialist"),
      // never the salon's.
      else Object.assign(hero, { variant: 'type', image: '', imageAlt: '' })
      kept.push({ ...b, content: hero })
      if (email.note) kept.push(textBlock(email.note))
    } else if (b.type === 'footer') {
      kept.push({ ...b, content: { ...b.content, preheader: email.preheader || '' } })
    }
  })
  const blocks = links ? resolveLocalLinks(kept, links) : kept
  const subject = email.subject || tpl.subject
  let html = ''
  try { html = renderEmail(blocks, { subject, sample: { FirstName: 'Sophie', SalonName: salonName || 'Your salon' } }) } catch (e) { html = '' }
  return { blocks, subject, preheader: email.preheader || '', html }
}

// What the campaign API is sent (the same shape the email creator sends)
export const emailPayload = (email, ctx) => {
  const { blocks, subject } = emailFor(email, ctx)
  let emailHtml
  try { emailHtml = renderEmail(blocks, { subject }) } catch (e) { emailHtml = undefined }
  return { type: 'email', subject, emailBlocks: blocks, ...(emailHtml ? { emailHtml } : {}) }
}

// A words-only email draft (Bailey's draft_campaign saves a subject and a
// body, no design) as blocks the email editor can open: the salon's template
// for what it's for, with the words in its lead section. Without this the
// editor opened a blank email and the words Bailey wrote were nowhere.
// Returns [] when there are no words to put in.
const PURPOSE_FOR_SEGMENT = { overdue: 'winback', inactive: 'winback', new: 'intro' }
export const blocksFromWords = (campaign, { market = 'hair', kit, links } = {}) => {
  const c = campaign || {}
  const body = String(c.message || '').trim()
  if (!body) return []
  const subject = String(c.subject || '').trim()
  const { blocks } = emailFor({
    templateId: templateFor(PURPOSE_FOR_SEGMENT[c.audienceSegment] || 'moment', market),
    subject,
    // Words only: the template's own label and picture (a new stylist, a
    // gift) belong to its example, not to this email
    hero: { variant: 'type', image: '', imageAlt: '', eyebrow: '', headline: subject, body, button: 'Book now', buttonUrl: '{{BookingLink}}' },
  }, { market, kit, links })
  return blocks
}

// The words in an email made with the old blocks (journey and reminder emails
// saved before the new templates): the first headline, the text under it and
// the first button. Pictures, prices and the old footer are left behind.
const plain = (s) => String(s || '')
  .replace(/<br\s*\/?>/gi, '\n')
  .replace(/<\/p>\s*<p[^>]*>/gi, '\n\n')
  .replace(/<[^>]+>/g, '')
  .replace(/&nbsp;/g, ' ')
  .replace(/&amp;/g, '&')
  .trim()

export const wordsFromClassic = (blocks) => {
  let headline = ''
  const body = []
  let button = ''
  let buttonUrl = ''
  ;(blocks || []).forEach((b) => {
    if (!b || !b.content || b.type === 'footer') return
    const c = b.content
    if (b.type === 'button') {
      if (!button) { button = plain(c.text); buttonUrl = c.url || '' }
      return
    }
    const head = plain(c.headline || c.title)
    if (head && !headline) headline = head.replace(/\s*\n\s*/g, ' ')
    const words = plain(c.subtext || c.body || c.description || (b.type === 'text' ? c.text : ''))
    if (words) body.push(words)
    if (!button && c.buttonText) { button = plain(c.buttonText); buttonUrl = c.buttonUrl || c.url || '' }
  })
  // A title written as a short text block (no headline field): the first
  // short line leads, the rest stays as the words under it
  if (!headline && body.length > 1 && body[0].length <= 80 && body[0].indexOf('\n') === -1) headline = body.shift()
  return { headline, body: body.join('\n\n'), button, buttonUrl }
}

// An old-blocks email as the salon's new template with the same words in its
// lead section, so it opens in the new editor. [] when there are no words.
export const blocksFromClassic = (blocks, subject, { market = 'hair', kit, links } = {}) => {
  const w = wordsFromClassic(blocks)
  if (!w.headline && !w.body) return []
  return emailFor({
    templateId: templateFor('moment', market),
    subject: String(subject || '').trim(),
    // Words only: the template's own label and picture belong to its purpose
    // (a gift, an offer), not to this email
    hero: {
      variant: 'type', image: '', imageAlt: '', eyebrow: '',
      headline: w.headline || String(subject || '').trim(),
      body: w.body,
      button: w.button || 'Book now',
      buttonUrl: w.buttonUrl || '{{BookingLink}}',
    },
  }, { market, kit, links }).blocks
}
